import cx from 'classnames'
import type { ButtonHTMLAttributes, DetailedHTMLProps } from 'react'
import { useRef, useEffect, useState } from 'react'

import { useCallsDialogContext } from '@src/app/voice/context/useCallsDialogContext'
import useKeyboardShortcuts from '@src/lib/hooks/useKeyboardShortcuts'
import Typography from '@ui/Typography'

import * as styles from './Keypad.css'
import SelectionHeader from './SelectionHeader'

interface KeypadProps {
  onKeyPressed: (digit: string) => void
  onClose: () => void
}

const Keypad = function ({ onKeyPressed, onClose }: KeypadProps) {
  const [input, setInput] = useState('')
  const { blurred } = useCallsDialogContext()
  const rootRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    rootRef.current?.focus()
  }, [rootRef])

  useKeyboardShortcuts({
    name: 'dialer/keypad',
    node: document,
    handler: (_, event) => {
      if (blurred) {
        return
      }

      const key = event.key
      if (keyMapping.find((m) => m.key === key)) {
        handleKey(key)
        event.preventDefault()
      }
    },
    dep: [blurred],
  })

  const handleKey = (digit: string) => {
    setInput((i) => i + digit)
    onKeyPressed(digit)
  }

  return (
    <div className={styles.root} ref={rootRef} tabIndex={-1}>
      <SelectionHeader title="" description="" handleBack={onClose} />

      <div className={styles.displayContainer}>
        <Typography variant="largeTitle" color="textPrimary" className={styles.input}>
          {input}
        </Typography>
      </div>

      <div className={styles.keysContainer}>
        {keyMapping.map((value) => (
          <Key
            key={value.key}
            title={value.title}
            onClick={() => handleKey(value.key)}
            subtitle={value.subtitle}
          />
        ))}
      </div>
    </div>
  )
}

interface KeyProps
  extends DetailedHTMLProps<ButtonHTMLAttributes<HTMLButtonElement>, HTMLButtonElement> {
  title: string
  subtitle: string | null
}

const Key = ({ title, subtitle, className, ...props }: KeyProps) => {
  return (
    <button {...props} className={cx(styles.key, className)}>
      <Typography
        variant="title3"
        color="textPrimary"
        fontWeight="medium"
        className={cx(styles.title, {
          [styles.asterisk]: title === '*',
          [styles.hashtag]: title === '#',
        })}
      >
        {title}
      </Typography>
      {subtitle !== null && (
        <Typography
          variant="caption3"
          color="textTertiary"
          fontWeight="medium"
          className={styles.subtitle}
        >
          {subtitle}
        </Typography>
      )}
    </button>
  )
}

export default Keypad

const keyMapping: readonly { key: string; title: string; subtitle: string | null }[] = [
  { key: '1', title: '1', subtitle: '' },
  { key: '2', title: '2', subtitle: 'A B C' },
  { key: '3', title: '3', subtitle: 'D E F' },
  { key: '4', title: '4', subtitle: 'G H I' },
  { key: '5', title: '5', subtitle: 'J K L' },
  { key: '6', title: '6', subtitle: 'M N O' },
  { key: '7', title: '7', subtitle: 'P Q R S' },
  { key: '8', title: '8', subtitle: 'T U V' },
  { key: '9', title: '9', subtitle: 'W X Y Z' },
  { key: '*', title: '*', subtitle: null },
  { key: '0', title: '0', subtitle: '＋' },
  { key: '#', title: '#', subtitle: null },
] as const
